// Утилиты для работы с фигурами - точно как в MiroLikeWhiteboard

// Проверка попадания точки в прямоугольник
export const isPointInRect = (x, y, shape) => {
  return x >= shape.x && x <= shape.x + (shape.width || shape.w) && 
         y >= shape.y && y <= shape.y + (shape.height || shape.h);
};

// Проверка попадания точки в круг
export const isPointInCircle = (x, y, shape) => {
  return Math.hypot(x - shape.x, y - shape.y) <= (shape.r || (shape.width/2));
};

// Проверка попадания точки в треугольник
export const isPointInTriangle = (x, y, shape) => {
  const p1 = [shape.x + shape.width/2, shape.y];
  const p2 = [shape.x + shape.width, shape.y + shape.height];
  const p3 = [shape.x, shape.y + shape.height];
  
  const area = Math.abs((p2[0] - p1[0]) * (p3[1] - p1[1]) - (p3[0] - p1[0]) * (p2[1] - p1[1])) / 2;
  const area1 = Math.abs((p1[0] - x) * (p2[1] - y) - (p2[0] - x) * (p1[1] - y)) / 2;
  const area2 = Math.abs((p2[0] - x) * (p3[1] - y) - (p3[0] - x) * (p2[1] - y)) / 2;
  const area3 = Math.abs((p3[0] - x) * (p1[1] - y) - (p1[0] - x) * (p3[1] - y)) / 2;
  
  return Math.abs(area - (area1 + area2 + area3)) < 0.1;
};

// Проверка попадания точки в стрелку или линию
export const isPointInLine = (x, y, shape) => {
  const dx = shape.x2 - shape.x1;
  const dy = shape.y2 - shape.y1;
  const length = Math.hypot(dx, dy);
  
  if (length === 0) return false;
  
  const t = Math.max(0, Math.min(1, ((x - shape.x1) * dx + (y - shape.y1) * dy) / (length * length)));
  const projX = shape.x1 + t * dx;
  const projY = shape.y1 + t * dy;
  
  return Math.hypot(x - projX, y - projY) <= 10;
};

// Проверка попадания точки в сообщение (с хвостиком)
export const isPointInMessage = (x, y, shape) => {
  // Основная область
  const inMainArea = x >= shape.x && x <= shape.x + shape.width && 
                     y >= shape.y && y <= shape.y + shape.height;
  if (inMainArea) return true;
  
  // Хвостик
  const tailPoints = [
    [shape.x + 24, shape.y + shape.height],
    [shape.x + 44, shape.y + shape.height],
    [shape.x + 34, shape.y + shape.height + 18]
  ];
  
  const area = Math.abs((tailPoints[1][0] - tailPoints[0][0]) * (tailPoints[2][1] - tailPoints[0][1]) - 
                       (tailPoints[2][0] - tailPoints[0][0]) * (tailPoints[1][1] - tailPoints[0][1])) / 2;
  const area1 = Math.abs((tailPoints[0][0] - x) * (tailPoints[1][1] - y) - (tailPoints[1][0] - x) * (tailPoints[0][1] - y)) / 2;
  const area2 = Math.abs((tailPoints[1][0] - x) * (tailPoints[2][1] - y) - (tailPoints[2][0] - x) * (tailPoints[1][1] - y)) / 2;
  const area3 = Math.abs((tailPoints[2][0] - x) * (tailPoints[0][1] - y) - (tailPoints[0][0] - x) * (tailPoints[2][1] - y)) / 2;
  
  return Math.abs(area - (area1 + area2 + area3)) < 0.1;
};

// Проверка попадания точки в текст
export const isPointInText = (x, y, shape) => {
  return x >= shape.x && x <= shape.x + 120 && 
         y >= shape.y - 24 && y <= shape.y;
};

// Проверка попадания точки в любую фигуру
export const isPointInShape = (x, y, shape) => {
  let result = false;
  switch (shape.type) {
    case 'rect':
      result = isPointInRect(x, y, shape);
      break;
    case 'sticker':
      result = isPointInRect(x, y, { ...shape, width: shape.w, height: shape.h });
      break;
    case 'circle':
      result = isPointInCircle(x, y, shape);
      break;
    case 'triangle':
      result = isPointInTriangle(x, y, shape);
      break;
    case 'arrow':
    case 'line':
      result = isPointInLine(x, y, shape);
      break;
    case 'message':
      result = isPointInMessage(x, y, shape);
      break;
    case 'text':
      result = isPointInText(x, y, shape);
      break;
    case 'pencil':
      // Для карандаша проверяем близость к точкам
      if (shape.points && shape.points.length > 0) {
        const tolerance = 8;
        result = shape.points.some(([px, py]) => 
          Math.abs(px - x) < tolerance && Math.abs(py - y) < tolerance
        );
      }
      break;
    case 'image':
    case 'video':
    case 'audio':
    case 'pdf':
    case 'youtube':
    case 'vimeo':
      result = isPointInRect(x, y, shape);
      break;
    default:
      result = false;
  }
  
  return result;
};

// Получение bounding box для фигуры
export const getShapeBoundingBox = (shape) => {
  switch (shape.type) {
    case 'rect':
      return { x: shape.x, y: shape.y, width: shape.width, height: shape.height };
    case 'sticker':
      return { x: shape.x, y: shape.y, width: shape.w, height: shape.h };
    case 'circle':
      const r = shape.r || (shape.width/2);
      return { x: shape.x - r, y: shape.y - r, width: 2*r, height: 2*r };
    case 'triangle':
      return { x: shape.x, y: shape.y, width: shape.width, height: shape.height };
    case 'message':
      return { x: shape.x, y: shape.y, width: shape.width, height: shape.height + 18 };
    case 'text':
      return { x: shape.x, y: shape.y - 24, width: shape.width || 200, height: shape.height || 64 };
    case 'line':
    case 'arrow':
      const xs = [shape.x1, shape.x2];
      const ys = [shape.y1, shape.y2];
      const minX = Math.min(...xs);
      const maxX = Math.max(...xs);
      const minY = Math.min(...ys);
      const maxY = Math.max(...ys);
      return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
    case 'pencil':
      const points = shape.points || [];
      if (points.length === 0) return { x: 0, y: 0, width: 0, height: 0 };
      const pointXs = points.map(p => p[0]);
      const pointYs = points.map(p => p[1]);
      const minPointX = Math.min(...pointXs);
      const maxPointX = Math.max(...pointXs);
      const minPointY = Math.min(...pointYs);
      const maxPointY = Math.max(...pointYs);
      return { x: minPointX, y: minPointY, width: maxPointX - minPointX, height: maxPointY - minPointY };
    case 'image':
    case 'video':
    case 'audio':
    case 'pdf':
    case 'youtube':
    case 'vimeo':
      return { x: shape.x, y: shape.y, width: shape.width, height: shape.height };
    default:
      return { x: 0, y: 0, width: 0, height: 0 };
  }
}; 